import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import RoleCard from "../../components/roles/role_card";
import { motion, AnimatePresence } from "framer-motion";
import SaveNotification from "../../components/notification/save_notification";
import Banner from "../../components/Banner";
import RecommendedRoles from "../../components/roles/recommended_roles";
import MatchRequestNotification from "../../components/notifications/MatchRequestNotification";
import { useMatchRequests } from '../../hooks/useMatchRequests'; // Ensure this path is correct
import StartupVisibilityModal from '../../components/modals/StartupVisibilityModal';
import WelcomeModal from '../../components/modals/WelcomeModal';
import HelpPanel from '../../components/HelpPanel';
import { useAuthRedirect } from '../../hooks/useAuthRedirect';
import { trackEngagementAction, trackEvent } from '../../lib/analytics';
import { trackRationaleApplicationSubmitted } from '../../lib/analytics';
import { setUserProperties } from '../../lib/analytics';
import { identifyUser } from '../../lib/analytics';
import { startSessionReplay, stopSessionReplay } from '../../lib/analytics';
import { clearInterviewsCache } from '../../lib/interviewsClient';



// === CLIENT-SIDE SEARCH AND FILTERING UTILITIES ===

/**
 * Normalize text for search - remove accents, convert to lowercase, etc.
 */
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^\w\s]/g, ' ') // Replace special chars with spaces
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Extract a usable video URL from Airtable-style field values.
 * Accepts:
 * - string URL
 * - array of strings
 * - attachment object(s) with { url } or { thumbnails: { ...: { url } } }
 */
const extractVideoIntroUrl = (videoField) => {
  if (!videoField) return "";

  if (typeof videoField === "string") {
    return videoField.trim();
  }

  if (Array.isArray(videoField)) {
    for (const item of videoField) {
      const url = extractVideoIntroUrl(item);
      if (url) return url;
    }
    return "";
  }

  if (typeof videoField === "object") {
    if (typeof videoField.url === "string" && videoField.url.trim()) {
      return videoField.url.trim();
    }
    const thumbs = videoField.thumbnails;
    if (thumbs && typeof thumbs === "object") {
      const sizes = ["full", "large", "small"];
      for (const size of sizes) {
        const t = thumbs[size];
        if (t && typeof t.url === "string" && t.url.trim()) {
          return t.url.trim();
        }
      }
    }
  }

  return "";
};

/**
 * Extract searchable text from a role object
 */
const extractSearchableText = (role) => {
  const fields = [
    role["Role Title"] || "",
    role["Name (from Startup)"] || "",
    role["Startup Description (from Startup)"] || "",
    role["Specific Experience"] || "",
    role["Job Description text"] || "",
    role["Location (from Startup)"] || "",
    role["Compensation"] || "",
    Array.isArray(role["Stream"]) ? role["Stream"].join(" ") : role["Stream"] || "",
    role["Working style category"] || "",
    role["Funding Stage (from Startup)"] || "",
    role["Team Size (from Startup)"] || "",
  ].join(" ");

  return normalizeText(fields);
};

/**
 * Calculate search relevance score for a role
 */
const calculateRelevanceScore = (role, searchTerms, searchableText) => {
  if (!searchTerms.length) return 1;

  let score = 0;
  const titleText = normalizeText(role["Role Title"] || "");
  const companyText = normalizeText(role["Name (from Startup)"] || "");

  searchTerms.forEach(term => {
    // Exact matches in title get highest score
    if (titleText.includes(term)) {
      score += 10;
    }
    // Exact matches in company name get high score
    if (companyText.includes(term)) {
      score += 8;
    }
    // Matches in other fields get lower score
    if (searchableText.includes(term)) {
      score += 3;
    }
    // Bonus for term at start of words
    const wordBoundaryRegex = new RegExp(`\\b${term}`, 'g');
    const wordMatches = searchableText.match(wordBoundaryRegex);
    if (wordMatches) {
      score += wordMatches.length * 2;
    }
  });

  return score;
};

/**
 * Fuzzy match for typo tolerance
 */
const fuzzyMatch = (text, term, threshold = 0.8) => {
  if (!text || !term) return false;

  // Simple fuzzy matching - can be enhanced with Levenshtein distance if needed
  const words = text.split(' ');
  return words.some(word => {
    if (word.length < 3) return word === term;

    let matches = 0;
    const minLength = Math.min(word.length, term.length);

    for (let i = 0; i < minLength; i++) {
      if (word[i] === term[i]) matches++;
    }

    return (matches / Math.max(word.length, term.length)) >= threshold;
  });
};

/**
 * Safely parse a D/M/YYYY date string into a Date object.
 * Handles cases where Airtable lookups return an array.
 */
const parseRoleDate = (dateString) => {
  if (!dateString) return null;

  // Airtable lookup fields can sometimes return arrays, take the first element
  if (Array.isArray(dateString)) {
    dateString = dateString[0];
  }

  if (typeof dateString !== 'string') return null;

  // Handle D/M/YYYY format
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
      // Month is 0-indexed in JavaScript Date constructor
      return new Date(year, month - 1, day);
    }
  }

  // Fallback for ISO strings or other standard date formats
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

const INTERVIEWS_CACHE_KEY_PREFIX = "candidate_interviews_";
const INTERVIEWS_CACHE_TTL = 5 * 60 * 1000; // REDUCED: 5 minutes (was 30 minutes)
const RECOMMENDED_ROLES_CACHE_PREFIX = "recommended_roles_cache_";
const RECOMMENDED_ROLES_CACHE_TTL = 5 * 60 * 1000; // REDUCED: 5 minutes (was 30 minutes)

/**
 * Utility function to clear all role-related caches
 * Call this when data seems corrupted or inconsistent
 */
const clearAllRoleCaches = () => {
  console.log("🧹 [CACHE CLEAR] Clearing all role-related caches...");
  const token = localStorage.getItem("token");
  const tokenPrefix = token ? token.slice(0, 32) : "";
  
  // List of cache keys to clear
  const cachesToClear = [
    `candidate_data_cache_${tokenPrefix}`,
    `${INTERVIEWS_CACHE_KEY_PREFIX}${tokenPrefix}`,
    `${RECOMMENDED_ROLES_CACHE_PREFIX}${tokenPrefix}`,
  ];
  
  // Also clear any roles cache with the candidate stream
  const allKeys = Object.keys(sessionStorage);
  allKeys.forEach(key => {
    if (key.startsWith('roles_cache_') || 
        key.startsWith('candidate_data_cache_') || 
        key.startsWith('candidate_interviews_') ||
        key.startsWith('recommended_roles_cache_')) {
      console.log("🧹 [CACHE CLEAR] Removing:", key);
      sessionStorage.removeItem(key);
    }
  });
  
  console.log("🧹 [CACHE CLEAR] All caches cleared");
};

/**
 * Advanced client-side filtering function
 */
const filterRoles = (roles, filters) => {
  const {
    searchQuery = "",
    workingStyle = [],
    savedOnly = false,
    shortlistedRoles = []
  } = filters;

  return roles.filter(role => {
    // 1. Stream filtering (this should already be done server-side)
    // No need to filter by stream again as we fetch based on candidate stream

    // 2. Working style filter
    if (workingStyle.length > 0) {
      const roleWorkingStyle = role["Working style category"];
      if (!roleWorkingStyle || !workingStyle.includes(roleWorkingStyle)) {
        return false;
      }
    }

    // 4. Saved only filter
    if (savedOnly) {
      if (!shortlistedRoles.includes(role.id)) {
        return false;
      }
    }

    // 5. Search query filter
    if (searchQuery.length > 0) {
      const searchableText = extractSearchableText(role);

      // Check if all search terms match (AND logic)
      const allTermsMatch = searchQuery.split(' ').every(term => {
        // Direct match
        if (searchableText.includes(term)) return true;

        // Fuzzy match for typo tolerance
        return fuzzyMatch(searchableText, term);
      });

      if (!allTermsMatch) return false;
    }

    return true;
  });
};

/**
 * Sort roles by relevance or date
 */
const sortRolesByRelevance = (roles, searchQuery) => {
  if (!searchQuery.trim()) {
    // Default sorting when no search - by most recent "Date of first role drop"
    return [...roles].sort((a, b) => {
      const dateA = parseRoleDate(a["Date of first role drop"]);
      const dateB = parseRoleDate(b["Date of first role drop"]);

      // Handle cases where dates might be missing
      if (!dateA && !dateB) {
        // If both missing, sort by role title
        const titleA = a["Role Title"] || "";
        const titleB = b["Role Title"] || "";
        return titleA.localeCompare(titleB);
      }
      if (!dateA) return 1; // Put roles without dates at the end
      if (!dateB) return -1; // Put roles without dates at the end

      // Sort by most recent date (newest first)
      return dateB - dateA;
    });
  }

  const searchTerms = normalizeText(searchQuery).split(' ').filter(term => term.length > 0);

  return [...roles]
    .map(role => ({
      ...role,
      _relevanceScore: calculateRelevanceScore(role, searchTerms, extractSearchableText(role))
    }))
    .sort((a, b) => {
      // Sort by relevance score first, then by most recent date
      if (b._relevanceScore !== a._relevanceScore) {
        return b._relevanceScore - a._relevanceScore;
      }

      // If relevance scores are equal, sort by most recent date
      const dateA = parseRoleDate(a["Date of first role drop"]);
      const dateB = parseRoleDate(b["Date of first role drop"]);

      if (!dateA && !dateB) {
        // If both missing, sort by role title
        const titleA = a["Role Title"] || "";
        const titleB = b["Role Title"] || "";
        return titleA.localeCompare(titleB);
      }
      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateB - dateA;
    })
    .map(role => {
      // Remove the temporary _relevanceScore property
      const { _relevanceScore, ...cleanRole } = role;
      return cleanRole;
    });
};

// === EXISTING COMPONENTS ===

// Add this component for showing application progress
const FloatingProgressBar = ({ appliedCount, target = 7 }) => {
  // DEBUGGING: Log the received count
  useEffect(() => {
    console.log("📊 [FloatingProgressBar] Rendered with appliedCount:", appliedCount, "target:", target);
    if (appliedCount > 20) {
      console.error("🚨 [FloatingProgressBar] CRITICAL: appliedCount is suspiciously high:", appliedCount);
    }
  }, [appliedCount, target]);

  // SAFETY: Cap the percentage to prevent UI issues with corrupted data
  const safeAppliedCount = Math.min(appliedCount, 100); // Cap at 100 to prevent overflow
  const percentage = Math.min((safeAppliedCount / target) * 100, 100);
  const isComplete = appliedCount >= target;

  // Dynamic color based on progress
  const getProgressColor = () => {
    if (isComplete) {
      return 'bg-gradient-to-r from-green-500 to-emerald-500';
    } else if (percentage >= 70) {
      return 'bg-gradient-to-r from-yellow-500 to-green-500';
    } else if (percentage >= 40) {
      return 'bg-gradient-to-r from-orange-500 to-yellow-500';
    } else {
      return 'bg-gradient-to-r from-red-400 to-orange-500';
    }
  };

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 
                    bg-white rounded-full shadow-lg border px-6 py-3 
                    min-w-[300px] transition-all hover:scale-105">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium">Weekly Applications</span>
            <span className={isComplete ? "text-green-600 font-bold" : "text-gray-600"}>
              {appliedCount}/{target}
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${getProgressColor()}`}
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>
        {!isComplete && (
          <span className="text-xs text-gray-500">
            {target - appliedCount} to go!
          </span>
        )}
        {isComplete && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-2xl"
          >
            🎉
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Early Applicant Banner component for role cards
const EarlyApplicantBanner = ({ show }) => {
  if (!show) return null;

  return (
    <div className="absolute top-0 left-0 right-0 bg-blue-50 border-b border-blue-100 p-2 text-xs text-blue-700 font-medium z-10 flex items-center">
      <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
      Apply Soon! Be an early applicant to stand out from the crowd.
    </div>
  );
};

/**
 * Helper hook for parsing the query string in the URL
 */
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/**
 * This component renders a modal that shows instructions
 * tailored to the candidate's stream (SWE, Sales, etc.).
 */
function HowToApplyModal({ isOpen, onClose, candidateStream }) {
  if (!isOpen) return null;

  const streamLower = (candidateStream || "").toLowerCase();
  const isSWE =
    streamLower.includes("swe") || streamLower.includes("software engineering");
  const isSales = streamLower.includes("sales");
  const isMarketingCandidate = streamLower.includes("marketing");
  const isGeneralistCandidate = streamLower.includes("generalist");

  const getInstructions = () => {
    if (isGeneralistCandidate && !isMarketingCandidate) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            👁️ Before you start: Watch this{" "}
            <a
              href="https://www.loom.com/share/aa408436d9574fc291d3301b2d4960e7?sid=dec38c05-34d4-47ce-8bb3-5abc1039af4b"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              quick video
            </a>{" "}
            from our Ops Director, Bethan!
          </p>
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>

          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <a
              href="https://jumpstart-alumni.notion.site/Jumpstart-Generalist-Stream-Hub-2024-September-update-8548d66aaaa947acaac10adf450272f1"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              Generalist and Marketing Notion Hub
            </a>
            . Good luck!
          </p>
        </div>
      );
    }

    if (isSWE) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>

          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <a
              href="https://jumpstart-alumni.notion.site/Making-your-choices-127b8f57dd8481749cb9eb95a3466840"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              Software Engineering Notion Hub
            </a>
            . Good luck!
          </p>
        </div>
      );
    }

    if (isSales) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            👁️ Before you start: Watch this{" "}
            <a
              href="https://www.loom.com/share/aa408436d9574fc291d3301b2d4960e7?sid=dec38c05-34d4-47ce-8bb3-5abc1039af4b"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              quick video
            </a>{" "}
            from our Ops Director, Bethan!
          </p>
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>

          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <a
              href="https://jumpstart-alumni.notion.site/Making-your-choices-e07a5efa0b7f4b6b8414bfb5812b1420?pvs=74"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              Sales Notion Hub
            </a>
            . Good luck!
          </p>
        </div>
      );
    }

    if (isMarketingCandidate) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            👁️ Before you start: Watch this{" "}
            <a
              href="https://www.loom.com/share/aa408436d9574fc291d3301b2d4960e7?sid=dec38c05-34d4-47ce-8bb3-5abc1039af4b"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              quick video
            </a>{" "}
            from our Ops Director, Bethan!
          </p>
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>

          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <a
              href="https://jumpstart-alumni.notion.site/Jumpstart-Generalist-Stream-Hub-2024-September-update-8548d66aaaa947acaac10adf450272f1"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              Generalist and Marketing Notion Hub
            </a>
            . Good luck!
          </p>
        </div>
      );
    }

    // Fallback copy
    return (
      <div className="space-y-3 sm:space-y-4">
        <p>🤓 Click 'Learn more' to explore roles & companies.</p>
        <p>
          🎥 Some roles include a video pitch. We recommend watching! It can
          really change your perspective on a company.
        </p>
        <p>🤔 Got questions? Email Kabir or check your Notion resources.</p>
        <p>
          ✅ Keen to apply? Submit your choices & 100 word rationale by Sunday
          midnight.
        </p>
        <p>
          📚 This page will expire Tuesday. Check your email for details.
        </p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        className="bg-white w-full max-w-xl p-4 sm:p-6 rounded-lg shadow-lg relative"
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 text-gray-400 hover:text-gray-600 p-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 8.586l4.95-4.95 1.414 1.414L11.414 10l4.95 4.95-1.414 1.414L10 11.414l-4.95 4.95-1.414-1.414L8.586 10l-4.95-4.95 1.414-1.414L10 8.586z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 pr-8">
          How to Apply
        </h2>
        <div className="overflow-y-auto max-h-[60vh] sm:max-h-[70vh] text-gray-700 text-xs sm:text-sm leading-relaxed pr-1">
          {getInstructions()}
        </div>
        <div className="mt-4 sm:mt-6 text-right">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#02B491] text-white rounded hover:bg-[#029078] transition-colors text-sm font-medium"
          >
            Got it
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const FilterButton = ({ label, active, onClick, ...props }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border-2 ${active
      ? "bg-[#02B491] border-[#02B491] text-white shadow-sm"
      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
      }`}
    {...props}
  >
    {label}
  </button>
);

function RolesList() {
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const { pendingCount, loading: matchRequestsLoading, refreshMatchRequests } = useMatchRequests();

  const [candidateStream, setCandidateStream] = useState("");
  const [candidateVideoUrl, setCandidateVideoUrl] = useState("");
  const [candidateVideoLoaded, setCandidateVideoLoaded] = useState(false);
  const [streamsArr, setStreamsArr] = useState([]);

  // Loading + progress bar
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  // Next deadline date (formatted)
  const [nextDeadlineDate, setNextDeadlineDate] = useState("");
  // Next roles release date
  const [nextReleaseDate, setNextReleaseDate] = useState("");
  // Reference to store the next release date object
  const nextReleaseRef = useRef(null);

  // Search & filters
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    workingStyle: [],
    savedOnly: false,
  });
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [tempWorkingStyle, setTempWorkingStyle] = useState([]);
  const [tempHasVideo, setTempHasVideo] = useState(false);
  const [tempSavedOnly, setTempSavedOnly] = useState(false);

  // CLIENT-SIDE DATA: All roles fetched once
  const [allRoles, setAllRoles] = useState([]);
  const [allRolesLoaded, setAllRolesLoaded] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  // View + UI states
  const [viewMode, setViewMode] = useState("grid");
  const [isSearchSticky, setIsSearchSticky] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showHowToApply, setShowHowToApply] = useState(false);

  // CLIENT-SIDE PAGINATION
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // CAROUSEL VIEW STATE
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Save notification
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [saveNotificationMessage, setSaveNotificationMessage] = useState("");

  // Shortlisted roles
  const [shortlistedRoles, setShortlistedRoles] = useState([]);
  const [shortlistedRolesLoaded, setShortlistedRolesLoaded] = useState(false);

  // NEW: State for candidate status
  const [candidateStatus, setCandidateStatus] = useState(null); // null initially, then 'Live' or other status

  // Recommended roles (Top Matches)
  const [recommendedRoles, setRecommendedRoles] = useState([]);
  const [recommendedRolesLoading, setRecommendedRolesLoading] = useState(true);
  const [recommendedRolesError, setRecommendedRolesError] = useState(null);

  // NEW: State for early applicant popup
  const [firstTimeLike, setFirstTimeLike] = useState({}); // Stores { roleId: true } if it's the first like

  // For canceling axios requests
  const axiosSource = useRef(null);
  const rolesRequestInFlight = useRef(false);

  // Add a ref for the "Explore Roles" section
  const exploreRolesRef = useRef(null);
  const rolesGridRef = useRef(null); // New ref for the roles grid
  const searchFilterRef = useRef(null);
  const stickyTriggerPoint = useRef(0);

  // State for "Apply Now" notifications
  const [applyNotification, setApplyNotification] = useState({ show: false, message: '', type: '' });

  // State for tracking roles the candidate has already applied to
  // NOTE: There are TWO different concepts here:
  // 1. appliedRolesThisWeek - ONLY roles applied this week (for FloatingProgressBar)
  // 2. allInteractedRoles - ALL roles ever interacted with (for filtering browse list)
  const [appliedRolesThisWeek, setAppliedRolesThisWeek] = useState([]);
  const [appliedRolesThisWeekLoaded, setAppliedRolesThisWeekLoaded] = useState(false);
  const [allInteractedRoles, setAllInteractedRoles] = useState([]);
  const [allInteractedRolesLoaded, setAllInteractedRolesLoaded] = useState(false);
  
  // Legacy state for backwards compatibility - will be set to allInteractedRoles
  const [appliedRoles, setAppliedRoles] = useState([]);
  const [appliedRolesLoaded, setAppliedRolesLoaded] = useState(false);

  // State for tracking roles where candidate is currently interviewing
  const [interviewingRoles, setInterviewingRoles] = useState([]);
  const [interviewingRolesLoaded, setInterviewingRolesLoaded] = useState(false);

  // State for tracking unsuccessful applications (to exclude from "applied" status)
  const [unsuccessfulRoles, setUnsuccessfulRoles] = useState([]);
  const [unsuccessfulRolesLoaded, setUnsuccessfulRolesLoaded] = useState(false);

  // State for tracking Application pending roles
  const [applicationPendingRoles, setApplicationPendingRoles] = useState([]);
  const [applicationPendingRolesLoaded, setApplicationPendingRolesLoaded] = useState(false);

  // State for hiding floating counter when near bottom
  const [hideFloatingCounter, setHideFloatingCounter] = useState(false);

  // State for match request notifications
  const [showMatchRequestModal, setShowMatchRequestModal] = useState(false);
  const [matchRequestNotification, setMatchRequestNotification] = useState({ show: false, message: '', type: '' });

  // State for first-time login startup visibility modal
  const [showStartupVisibilityModal, setShowStartupVisibilityModal] = useState(false);
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);

  // State for welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [userName, setUserName] = useState("");
  const [showVideoRequiredModal, setShowVideoRequiredModal] = useState(false);
  const [showVideoWarningModal, setShowVideoWarningModal] = useState(false);
  const [pendingApply, setPendingApply] = useState(null);
  const [isApplyingFromModal, setIsApplyingFromModal] = useState(false);



  // 1) Ensure user is logged in
  const { isAuthenticated, isChecking } = useAuthRedirect();

  const clearRecommendedRolesCache = useCallback(() => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        sessionStorage.removeItem(`${RECOMMENDED_ROLES_CACHE_PREFIX}${token.slice(0, 32)}`);
      }
    } catch (_) { }
  }, []);

  const fetchRecommendedRoles = useCallback(async ({ bypassCache = false } = {}) => {
    if (!isAuthenticated || isChecking) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setRecommendedRoles([]);
        setRecommendedRolesError("Login required");
        setRecommendedRolesLoading(false);
        return;
      }

      const cacheKey = `${RECOMMENDED_ROLES_CACHE_PREFIX}${token.slice(0, 32)}`;

      if (!bypassCache) {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const { timestamp, data } = JSON.parse(cachedData);
            if (Date.now() - timestamp < RECOMMENDED_ROLES_CACHE_TTL) {
              setRecommendedRoles(Array.isArray(data) ? data : []);
              setRecommendedRolesError(null);
              setRecommendedRolesLoading(false);
              return;
            }
          } catch (e) {
            sessionStorage.removeItem(cacheKey);
          }
        }
      }

      setRecommendedRolesLoading(true);
      const requestConfig = {
        headers: { Authorization: `Bearer ${token}` }
      };
      if (bypassCache) {
        requestConfig.params = { forceRefresh: "true" };
      }
      const response = await axios.get("/api/candidates/getRecommendedRoles", requestConfig);
      const recommendations = response.data?.recommendedRoles || [];
      setRecommendedRoles(recommendations);
      setRecommendedRolesError(null);
      sessionStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        data: recommendations
      }));
    } catch (error) {
      console.error("Error fetching recommended roles:", error);
      setRecommendedRolesError("Error loading matches");
    } finally {
      setRecommendedRolesLoading(false);
    }
  }, [isAuthenticated, isChecking]);

  const fetchCandidateFresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const response = await axios.get("/api/candidates/fetchCandidate", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const fields = response?.data?.candidate?.fields || null;
    if (!fields) return null;
    // Update shared candidate cache to avoid repeated stale reads
    try {
      const cacheKey = `candidate_data_cache_${token.slice(0, 32)}`;
      sessionStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), fields }));
    } catch (_) {}
    return fields;
  }, []);

  // 2) Fetch candidate stream
  useEffect(() => {
    // Ensure Session Replay is active while viewing this page
    try { startSessionReplay(); } catch (_) { }
    
    // DEBUG: Log page load and cache status
    console.log("═══════════════════════════════════════════════════════");
    console.log("🚀 [PAGE LOAD] roles_list.js mounted");
    console.log("📅 [PAGE LOAD] Timestamp:", new Date().toISOString());
    
    // Log all session storage keys related to roles/interviews
    const cacheKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('roles_cache_') || 
      key.includes('candidate_data_cache_') || 
      key.includes('candidate_interviews_') ||
      key.includes('recommended_roles_cache_')
    );
    console.log("📦 [PAGE LOAD] Active caches:", cacheKeys);
    
    // Log cache sizes to help identify bloated caches
    cacheKeys.forEach(key => {
      try {
        const data = sessionStorage.getItem(key);
        if (data) {
          const size = new Blob([data]).size;
          console.log(`  - ${key}: ${(size / 1024).toFixed(1)} KB`);
        }
      } catch (_) {}
    });
    console.log("═══════════════════════════════════════════════════════");
    
    return () => { try { stopSessionReplay(); } catch (_) { } };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    // Defer recommendations fetch to reduce initial load on Airtable
    const timer = setTimeout(() => {
      fetchRecommendedRoles();
    }, 1000); // Wait 1 second after critical data loads
    return () => clearTimeout(timer);
  }, [isAuthenticated, isChecking, fetchRecommendedRoles]);

  // 3) Fetch candidate stream
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;

    const fetchCandidateStream = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        // CACHE CHECK: candidate data - REDUCED TTL to 5 minutes (was 1 hour)
        const cacheKey = `candidate_data_cache_${token.slice(0, 32)}`;
        let candidateFields = null;
        const cachedData = sessionStorage.getItem(cacheKey);

        if (cachedData) {
          try {
            const { timestamp, fields } = JSON.parse(cachedData);
            const cacheAge = Date.now() - timestamp;
            if (cacheAge < 5 * 60 * 1000) { // 5 minute TTL (reduced from 1 hour)
              candidateFields = fields;
            } else {
              sessionStorage.removeItem(cacheKey);
            }
          } catch (e) { 
            sessionStorage.removeItem(cacheKey); 
          }
        }

        if (!candidateFields) {
          const response = await axios.get("/api/candidates/fetchCandidate", {
            headers: { Authorization: `Bearer ${token}` },
          });
          candidateFields = response.data.candidate.fields;
          // Cache the result
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({
              timestamp: Date.now(),
              fields: candidateFields
            }));
          } catch (e) { }
        }

        let streamValue =
          candidateFields["Do not edit - new Stream (for visibility column)"] ||
          "";

        if (Array.isArray(streamValue)) {
          streamValue = streamValue.length > 0 ? streamValue[0] : "";
        }
        setCandidateStream(streamValue);
        // Capture candidate video intro URL for pre-apply guard
        const videoField =
          candidateFields["Video intro (new)"] ||
          candidateFields["Raw video intro"] ||
          "";
        setCandidateVideoUrl(extractVideoIntroUrl(videoField));
        setCandidateVideoLoaded(true);
        try {
          // Update Mixpanel user profile with stream for breakdowns
          setUserProperties({ stream: streamValue });
        } catch (_) { }

        // Identify user by email to ensure correct distinct_id for returning sessions
        const emailFromProfile = candidateFields["Email"] || candidateFields["email"] || "";
        if (emailFromProfile) {
          try {
            identifyUser(emailFromProfile, { "$email": emailFromProfile, stream: streamValue });
          } catch (_) { }
        }
      } catch (error) {
        console.error("Error fetching candidate stream:", error);
        setCandidateVideoLoaded(true); // prevent hard-blocking if fetch fails
      }
    };
    fetchCandidateStream();
  }, [isAuthenticated, isChecking]);

  // 3) Convert candidateStream => streamsArr
  useEffect(() => {
    if (!candidateStream) {
      setStreamsArr([]);
      return;
    }
    const arr = candidateStream.split(",").map((s) => s.trim().toLowerCase());
    setStreamsArr(arr);
  }, [candidateStream]);

  // === CLIENT-SIDE FILTERING AND PAGINATION ===

  // Memoized filtered and sorted roles
  const filteredAndSortedRoles = useMemo(() => {
    if (!allRolesLoaded || allRoles.length === 0) return [];

    // CRITICAL: Wait for ALL exclusion data to be loaded before filtering
    // This prevents the race condition where we show 45 roles, then 12 roles
    if (!allInteractedRolesLoaded) {
      console.log('⏳ [FILTER] Waiting for allInteractedRoles to load before filtering...');
      return [];
    }

    console.log('🔍 [FILTER] Client-side filtering - Input:', {
      totalRoles: allRoles.length,
      searchQuery,
      activeFilters,
      shortlistedRoles: shortlistedRoles.length
    });

    // DEBUGGING: Log the exclusion arrays
    console.log('🔍 [FILTER] Exclusion arrays:', {
      appliedRoles: appliedRoles.length,
      allInteractedRoles: allInteractedRoles.length,
      interviewingRoles: interviewingRoles.length,
      unsuccessfulRoles: unsuccessfulRoles.length,
      applicationPendingRoles: applicationPendingRoles.length,
      allInteractedRolesLoaded,
      interviewingRolesLoaded,
      unsuccessfulRolesLoaded,
      applicationPendingRolesLoaded
    });

    // First, filter out roles that candidate has already interacted with
    // (applied, interviewing, rejected, or application pending)
    let excludedApplied = 0;
    let excludedInterviewing = 0;
    let excludedUnsuccessful = 0;
    let excludedPending = 0;
    
    const browsableRoles = allRoles.filter(role => {
      const roleId = role.id;

      // Exclude if already applied
      if (appliedRoles.includes(roleId)) {
        excludedApplied++;
        return false;
      }

      // Exclude if currently interviewing
      if (interviewingRoles.includes(roleId)) {
        excludedInterviewing++;
        return false;
      }

      // Exclude if rejected/unsuccessful
      if (unsuccessfulRoles.includes(roleId)) {
        excludedUnsuccessful++;
        return false;
      }

      // Exclude if application pending
      if (applicationPendingRoles.includes(roleId)) {
        excludedPending++;
        return false;
      }

      return true;
    });

    console.log('🔍 [FILTER] Roles excluded from browse:', {
      totalRoles: allRoles.length,
      browsableRoles: browsableRoles.length,
      excludedApplied,
      excludedInterviewing,
      excludedUnsuccessful,
      excludedPending,
      totalExcluded: excludedApplied + excludedInterviewing + excludedUnsuccessful + excludedPending
    });
    
    // DEBUGGING: If nothing was excluded but we have applied roles, something is wrong
    if (appliedRoles.length > 0 && excludedApplied === 0) {
      console.warn('⚠️ [FILTER WARNING] We have appliedRoles but none were excluded!');
      console.warn('⚠️ [FILTER WARNING] First 5 appliedRoles:', appliedRoles.slice(0, 5));
      console.warn('⚠️ [FILTER WARNING] First 5 allRoles IDs:', allRoles.slice(0, 5).map(r => r.id));
    }

    const filtered = filterRoles(browsableRoles, {
      searchQuery,
      workingStyle: activeFilters.workingStyle,
      savedOnly: activeFilters.savedOnly,
      shortlistedRoles
    });

    const sorted = sortRolesByRelevance(filtered, searchQuery);

    console.log('🔍 [FILTER] Client-side filtering - Output:', {
      filteredCount: sorted.length,
      hasSearchQuery: !!searchQuery.trim()
    });

    return sorted;
  }, [allRoles, allRolesLoaded, searchQuery, activeFilters, shortlistedRoles, appliedRoles, allInteractedRoles, allInteractedRolesLoaded, interviewingRoles, unsuccessfulRoles, applicationPendingRoles, interviewingRolesLoaded, unsuccessfulRolesLoaded, applicationPendingRolesLoaded]);

  // Memoized pagination
  const paginatedRoles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedRoles.slice(startIndex, endIndex);
  }, [filteredAndSortedRoles, currentPage, pageSize]);

  // Calculate pagination info
  const totalRolesCount = filteredAndSortedRoles.length;
  const totalPages = Math.ceil(totalRolesCount / pageSize);
  const hasMore = currentPage < totalPages;

  // Track top-row view of roles list using IntersectionObserver (fires once per page view)
  const topRowRefs = useRef([]);
  const hasFiredTopRowView = useRef(false);
  // Reset refs array size when page changes
  topRowRefs.current = topRowRefs.current.slice(0, 3);

  useEffect(() => {
    if (hasFiredTopRowView.current) return;
    if (!paginatedRoles || paginatedRoles.length === 0) return;
    const topCount = Math.min(3, paginatedRoles.length);
    const observed = topRowRefs.current.slice(0, topCount).filter(Boolean);
    if (observed.length === 0) return;

    const visibleSet = new Set();
    const requiredVisible = viewMode === 'grid' ? Math.min(2, topCount) : 1; // grid: at least 2 of first row; list: first item

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute('data-top-index'));
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            visibleSet.add(idx);
          } else {
            visibleSet.delete(idx);
          }
        });
        if (!hasFiredTopRowView.current && visibleSet.size >= requiredVisible) {
          hasFiredTopRowView.current = true;
          try {
            trackEvent('Roles List Viewed', {
              view_mode: viewMode,
              top_row_count: topCount,
              visible_count: visibleSet.size,
              role_ids: paginatedRoles.slice(0, topCount).map((r) => r.id),
            });
          } catch (_) { }
          observer.disconnect();
        }
      },
      { threshold: [0.5] }
    );

    observed.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [paginatedRoles, viewMode]);

  // Animate progress bar while loading
  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 5 : prev));
      }, 200);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);


  // ---- UPDATED CALCULATION LOGIC FOR FORTNIGHTLY MONDAY DEADLINES WITH EXTENDED VISIBILITY ----
  const calculateNextDeadline = () => {
    // Current date
    const currentDate = new Date();
    const nextSunday = new Date(currentDate);

    // Calculate days until next Sunday
    // Sunday is 0, Monday is 1, ..., Saturday is 6
    const daysUntilSunday = (7 - currentDate.getDay()) % 7;

    // If today is Sunday, and it's not past midnight (effectively meaning we want *next* Sunday unless it's very early Sunday)
    // For simplicity, we'll always aim for the *upcoming* Sunday. If today is Sunday, it will be *this* Sunday.
    nextSunday.setDate(currentDate.getDate() + daysUntilSunday);

    // Set to midnight (end of Sunday)
    nextSunday.setHours(23, 59, 59, 999);

    // Format the final date
    const day = nextSunday.getDate();
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    if (day === 2 || day === 22) suffix = "nd";
    if (day === 3 || day === 23) suffix = "rd";

    const monthsShort = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const month = monthsShort[nextSunday.getMonth()];

    // Deadline is always Sunday
    const dayOfWeek = "Sunday";

    setNextDeadlineDate(`${dayOfWeek} ${day}${suffix} ${month}`);
  };
  // Calculate next roles release date - returns a Date object
  const calculateNextRolesRelease = () => {
    // Start date: 3rd April 2025 (Thursday), 6:00 PM
    const startDate = new Date(2025, 3, 3);
    startDate.setHours(18, 0, 0, 0);

    const currentDate = new Date();
    const daysSinceStart = Math.floor(
      (currentDate - startDate) / (1000 * 60 * 60 * 24)
    );
    const fortnightsPassed = Math.floor(daysSinceStart / 14);

    const nextRelease = new Date(startDate);
    nextRelease.setDate(startDate.getDate() + fortnightsPassed * 14);
    if (nextRelease < currentDate) {
      nextRelease.setDate(nextRelease.getDate() + 14);
    }

    return nextRelease;
  };

  // Format the release date as a string
  const formatReleaseDate = (dateObj) => {
    const day = dateObj.getDate();
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    if (day === 2 || day === 22) suffix = "nd";
    if (day === 3 || day === 23) suffix = "rd";

    const monthsShort = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthsShort[dateObj.getMonth()];

    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayOfWeek = daysOfWeek[dateObj.getDay()];

    return `${dayOfWeek}, ${day}${suffix} ${month}`;
  };

  // Run on mount to set nextDeadlineDate + nextReleaseDate
  useEffect(() => {
    calculateNextDeadline();

    const nextRelease = calculateNextRolesRelease();
    setNextReleaseDate(formatReleaseDate(nextRelease));
    nextReleaseRef.current = nextRelease;
  }, []);

  // Use useLayoutEffect to get an accurate position for the sticky trigger point
  // after the layout has been calculated, preventing jumps and glitches.
  useLayoutEffect(() => {
    if (searchFilterRef.current) {
      stickyTriggerPoint.current = searchFilterRef.current.offsetTop;
    }
  }, [allRolesLoaded]); // Recalculate if the roles loading changes the layout

  // This effect now ONLY handles setting the isSearchSticky state and floating counter visibility.
  useEffect(() => {
    const handleScroll = () => {
      // We check against the stable trigger point calculated in useLayoutEffect
      if (stickyTriggerPoint.current > 0) {
        setIsSearchSticky(window.scrollY > stickyTriggerPoint.current);
      }

      // Hide floating counter when user is near the bottom of the page
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

      // Hide counter when within 200px of bottom to avoid blocking pagination
      setHideFloatingCounter(distanceFromBottom < 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency array ensures this listener is set up only once.

  // This separate effect handles the visibility of the filters based on stickiness.
  // This separation of concerns makes the logic cleaner and more reliable.
  useEffect(() => {
    if (isSearchSticky) {
      // When the search bar is sticky (scrolled down), hide the filters.
      setShowFilters(false);
    } else {
      // When not sticky (scrolled up), show the filters.
      setShowFilters(true);
    }
  }, [isSearchSticky]);

  // Load the user's shortlisted roles AND status AND stream
  useEffect(() => {
    const fetchCandidateData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        // CACHE CHECK: candidate data (Reuse same cache key)
        // REDUCED TTL from 1 hour to 5 minutes to prevent stale data issues
        const cacheKey = `candidate_data_cache_${token.slice(0, 32)}`;
        let candidateFields = null;
        const cachedData = sessionStorage.getItem(cacheKey);

        if (cachedData) {
          try {
            const { timestamp, fields } = JSON.parse(cachedData);
            const cacheAge = Date.now() - timestamp;
            // REDUCED: 5 minute TTL instead of 1 hour
            if (cacheAge < 5 * 60 * 1000) {
              candidateFields = fields;
            } else {
              sessionStorage.removeItem(cacheKey);
            }
          } catch (e) { 
            sessionStorage.removeItem(cacheKey); 
          }
        }

        if (!candidateFields) {
          const response = await axios.get("/api/candidates/fetchCandidate", {
            headers: { Authorization: `Bearer ${token}` },
          });
          candidateFields = response.data.candidate.fields;
          // Cache the result
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({
              timestamp: Date.now(),
              fields: candidateFields
            }));
            console.log("📦 [CACHE SET] Cached fresh candidate data");
          } catch (e) { 
            console.log("📦 [CACHE ERROR] Failed to cache candidate data:", e.message);
          }
        }

        // Set Shortlisted Roles - FIX: Ensure it's always an array even if null
        const shortlisted = candidateFields["Shortlisted Roles"];
        setShortlistedRoles(Array.isArray(shortlisted) ? shortlisted : []);
        setShortlistedRolesLoaded(true);

        // Set Candidate Status
        const statusValue =
          candidateFields["Jumpstart Candidate Status (from CRM)"];

        let statusString = "Unknown"; // Default
        if (Array.isArray(statusValue) && statusValue.length > 0) {
          statusString = statusValue[0]; // Take the first element if it's an array
        } else if (typeof statusValue === 'string' && statusValue.trim() !== '') {
          statusString = statusValue; // Use directly if it's a non-empty string
        }

        setCandidateStatus(statusString);
        console.log("Candidate Status (Processed):", statusString); // Updated Debugging

        // Check if this is a first-time login from localStorage (set during login)
        const isFirstTimeFromStorage = localStorage.getItem("isFirstTimeLogin") === "true";
        setIsFirstTimeLogin(isFirstTimeFromStorage);
        console.log("Is first time login (from localStorage):", isFirstTimeFromStorage);

        // Get user's name for welcome modal
        const userFullName = candidateFields["Name"] || "";
        setUserName(userFullName.split(" ")[0]);

        // Set Candidate Stream
        let streamValue =
          candidateFields[
          "Do not edit - new Stream (for visibility column)"
          ] || "";
        if (Array.isArray(streamValue)) {
          streamValue = streamValue.length > 0 ? streamValue[0] : "";
        }
        setCandidateStream(streamValue);

        // Set Applied Roles THIS WEEK from "Applied this week drop" field
        // This is ONLY for the FloatingProgressBar weekly count
        const appliedToThisWeek = candidateFields["Applied this week drop"];
        
        // Make sure it's always an array, even if null
        const weeklyAppliedRoleIds = Array.isArray(appliedToThisWeek) ? appliedToThisWeek : [];
        
        // VALIDATION: Check for suspicious data (weekly should typically be < 20)
        if (weeklyAppliedRoleIds.length > 20) {
          console.warn("⚠️ [WARNING] Suspiciously high WEEKLY applied roles count:", weeklyAppliedRoleIds.length);
          console.warn("⚠️ [WARNING] First 5 IDs:", weeklyAppliedRoleIds.slice(0, 5));
          console.warn("⚠️ [WARNING] Clearing ALL caches due to potentially corrupted data...");
          clearAllRoleCaches();
        }
        
        console.log("✅ [WEEKLY APPLIED] Setting appliedRolesThisWeek from Airtable, count:", weeklyAppliedRoleIds.length);
        setAppliedRolesThisWeek(weeklyAppliedRoleIds);
        setAppliedRolesThisWeekLoaded(true);
        
        // Also set appliedRoles to weekly for now - will be expanded by interview data later
        setAppliedRoles(weeklyAppliedRoleIds);
        setAppliedRolesLoaded(true);
      } catch (error) {
        console.error(
          "Error fetching candidate data (status/stream/shortlist):",
          error
        );
        setShortlistedRolesLoaded(true); // Still mark as loaded even on error
        setAppliedRolesLoaded(true); // Also mark applied roles as loaded
        setCandidateStatus("Error"); // Indicate an error fetching status
      }
    };
    fetchCandidateData();
  }, [navigate]);

  // We'll still load interviews, but as a supplement to the "Applied this week drop" field
  useEffect(() => {
    const fetchCandidateApplications = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        // Only proceed if we've already loaded the initial appliedRoles from the candidate record
        if (!appliedRolesLoaded) return;

        // Defer interview data fetch to reduce initial concurrent load
        await new Promise(resolve => setTimeout(resolve, 500));

        // CACHE CHECK: interview data (supplemental applied roles)
        // REDUCED TTL from 30 minutes to 5 minutes
        const REDUCED_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
        const cacheKey = token
          ? `${INTERVIEWS_CACHE_KEY_PREFIX}${token.slice(0, 32)}`
          : null;
        let interviewData = null;
        const cachedData = cacheKey ? sessionStorage.getItem(cacheKey) : null;

        if (cachedData) {
          try {
            const { timestamp, data } = JSON.parse(cachedData);
            const cacheAge = Date.now() - timestamp;
            if (cacheAge < REDUCED_CACHE_TTL) {
              interviewData = data;
              console.log("📦 [CACHE HIT] Using cached interview data, age:", Math.round(cacheAge / 1000), "seconds");
            } else {
              console.log("📦 [CACHE EXPIRED] Interview cache expired, age:", Math.round(cacheAge / 1000), "seconds");
              if (cacheKey) sessionStorage.removeItem(cacheKey);
            }
          } catch (e) {
            console.log("📦 [CACHE ERROR] Failed to parse interview cache, clearing");
            if (cacheKey) sessionStorage.removeItem(cacheKey);
          }
        }

        if (!interviewData) {
          console.log("📡 [FETCH] Fetching fresh interview data from API...");
          const response = await axios.get("/api/interviews/fetchInterviews", {
            headers: { Authorization: `Bearer ${token}` }
          });
          interviewData = response.data.interviews || [];
          console.log("📡 [FETCH] Received", interviewData.length, "interviews from API");
          if (cacheKey) {
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify({
                timestamp: Date.now(),
                data: interviewData
              }));
              console.log("📦 [CACHE SET] Cached fresh interview data");
            } catch (e) { 
              console.log("📦 [CACHE ERROR] Failed to cache interview data:", e.message);
            }
          }
        }

        const unsuccessfulStages = [
          "Rejected @ CV",
          "Rejected @ Interview",
          "Offer rejected",
          "Offer retracted",
          "Startup withdrawn",
          "Candidate withdrawn",
          "Rejected Interview Request",
        ];

        const extraAppliedRoleIds = [];
        const interviewingRoleIds = [];
        const applicationPendingRoleIds = [];
        const unsuccessfulRoleIds = [];
        const dedupeIds = (arr) => Array.from(new Set(arr.filter(Boolean)));

        // Extract role IDs from the interviews and bucket by stage
        (interviewData || []).forEach(interview => {
          if (!interview?.fields) return;

          const roleField = interview.fields.Role;
          const roleId = Array.isArray(roleField) ? roleField[0] : roleField;
          if (!roleId) return;

          extraAppliedRoleIds.push(roleId);

          const stage = interview.fields.Stage;
          if (stage === "Interviewing") {
            interviewingRoleIds.push(roleId);
          } else if (stage === "Application pending") {
            applicationPendingRoleIds.push(roleId);
          } else if (unsuccessfulStages.includes(stage)) {
            unsuccessfulRoleIds.push(roleId);
          }
        });

        console.log("🔍 [DEBUG] Interview data processing:");
        console.log("  - Total interviews (ALL TIME):", (interviewData || []).length);
        console.log("  - All interacted role IDs:", extraAppliedRoleIds.length);
        console.log("  - Interviewing roles:", interviewingRoleIds.length);
        console.log("  - Application pending roles:", applicationPendingRoleIds.length);
        console.log("  - Unsuccessful roles:", unsuccessfulRoleIds.length);

        // Set allInteractedRoles for filtering (this is ALL historical data)
        const uniqueInteractedRoles = dedupeIds(extraAppliedRoleIds);
        console.log("✅ [ALL INTERACTED] Setting allInteractedRoles for filtering, count:", uniqueInteractedRoles.length);
        setAllInteractedRoles(uniqueInteractedRoles);
        setAllInteractedRolesLoaded(true);
        
        // Set appliedRoles to ALL interacted roles (for filtering browse list)
        // This is DIFFERENT from appliedRolesThisWeek which is only for the progress bar
        setAppliedRoles(uniqueInteractedRoles);
        console.log("✅ [APPLIED ROLES] Set to allInteractedRoles for filtering, count:", uniqueInteractedRoles.length);

        // Update stage-tracking arrays using the same dataset
        setInterviewingRoles(dedupeIds(interviewingRoleIds));
        setInterviewingRolesLoaded(true);

        setApplicationPendingRoles(dedupeIds(applicationPendingRoleIds));
        setApplicationPendingRolesLoaded(true);

        setUnsuccessfulRoles(dedupeIds(unsuccessfulRoleIds));
        setUnsuccessfulRolesLoaded(true);
      } catch (error) {
        console.error("Error fetching candidate applications:", error);
        setInterviewingRolesLoaded(true);
        setApplicationPendingRolesLoaded(true);
        setUnsuccessfulRolesLoaded(true);
      }
    };

    fetchCandidateApplications();
  }, [navigate, appliedRolesLoaded]);

  // Removed redundant fetchInterviews call - stage data now derived in fetchCandidateApplications

  // Show welcome modal first for first-time login, then startup visibility modal
  useEffect(() => {
    console.log("Checking if should show welcome modal:", {
      isFirstTimeLogin,
      candidateStatus,
      shouldShow: isFirstTimeLogin && candidateStatus === "Live"
    });

    if (isFirstTimeLogin && candidateStatus === "Live") {
      console.log("Showing welcome modal for first-time login");
      setShowWelcomeModal(true);
    }
  }, [isFirstTimeLogin, candidateStatus]);

  // Show startup visibility modal after welcome modal is closed
  useEffect(() => {
    console.log("Checking if should show startup visibility modal:", {
      isFirstTimeLogin,
      candidateStatus,
      shouldShow: isFirstTimeLogin && candidateStatus === "Live"
    });

    if (isFirstTimeLogin && candidateStatus === "Live" && !showWelcomeModal) {
      console.log("Showing startup visibility modal for first-time login");
      setShowStartupVisibilityModal(true);
    }
  }, [isFirstTimeLogin, candidateStatus, showWelcomeModal]);

  // Reset loading state when video warning modal closes
  useEffect(() => {
    if (!showVideoWarningModal) {
      setIsApplyingFromModal(false);
    }
  }, [showVideoWarningModal]);

  // Keep temp filter inputs in sync
  useEffect(() => {
    setTempSearchQuery(searchQuery);
    setTempWorkingStyle(activeFilters.workingStyle);
    setTempHasVideo(activeFilters.hasVideo);
    setTempSavedOnly(activeFilters.savedOnly);
  }, [searchQuery, activeFilters]);

  // Instant search - apply search as user types (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (tempSearchQuery !== searchQuery) {
        setSearchQuery(tempSearchQuery);
        setCurrentPage(1);
        // record search usage
        const chips = [
          ...(activeFilters.savedOnly ? ["Saved"] : []),
          ...activeFilters.workingStyle,
        ];
        trackEngagementAction("search_used", {
          query: tempSearchQuery,
          chips,
        });
        try {
          trackEvent('Search or Filter Used', {
            action: 'search',
            query: tempSearchQuery,
            chips,
            page: 'roles_list',
          });
        } catch (_) { }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [tempSearchQuery, searchQuery, activeFilters]);

  const handleApplyFilters = () => {
    const sanitizedQuery = tempSearchQuery.replace(/\s+/g, " ").trim();

    // Always apply filters - client-side filtering is instant
    setSearchQuery(sanitizedQuery);
    setActiveFilters({
      workingStyle: tempWorkingStyle,
      savedOnly: tempSavedOnly,
    });
    setCurrentPage(1); // Reset to first page when filters change
    const chips = [
      ...(tempSavedOnly ? ["Saved"] : []),
      ...tempWorkingStyle,
    ];
    trackEngagementAction("filters_applied", { query: sanitizedQuery, chips });
    try {
      trackEvent('Search or Filter Used', {
        action: 'filters_applied',
        query: sanitizedQuery,
        chips,
        page: 'roles_list',
      });
    } catch (_) { }
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setTempSearchQuery("");
    setActiveFilters({
      workingStyle: [],
      savedOnly: false,
    });
    setCurrentPage(1);
    trackEngagementAction("filters_cleared", {});
    try {
      trackEvent('Search or Filter Used', {
        action: 'filters_cleared',
        query: '',
        chips: [],
        page: 'roles_list',
      });
    } catch (_) { }
  };

  /**
   * FETCH ALL ROLES ONCE - CLIENT-SIDE FILTERING
   */
  const fetchAllRoles = async () => {
    try {
      // Prevent duplicate concurrent requests
      if (rolesRequestInFlight.current) {
        console.log("Roles request already in flight, skipping duplicate");
        return;
      }

      if (axiosSource.current) {
        axiosSource.current.cancel("Operation canceled due to new request.");
      }

      // CACHE CHECK: REDUCED to 5 minutes (was 10 minutes)
      const cacheKey = `roles_cache_${candidateStream}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const { timestamp, roles } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;
          const isFresh = cacheAge < 5 * 60 * 1000; // 5 minutes (reduced from 10)

          if (isFresh && roles && roles.length > 0) {
            console.log("📦 [ROLES CACHE HIT] Using cached roles, count:", roles.length, "age:", Math.round(cacheAge / 1000), "seconds");
            setAllRoles(roles);
            setAllRolesLoaded(true);
            setProgress(100);
            setLoading(false);
            return;
          } else {
            console.log("📦 [ROLES CACHE EXPIRED] Cache age:", Math.round(cacheAge / 1000), "seconds, clearing...");
            sessionStorage.removeItem(cacheKey);
          }
        } catch (e) {
          console.error("📦 [ROLES CACHE ERROR] Error parsing cached roles, clearing...", e);
          sessionStorage.removeItem(cacheKey);
        }
      } else {
        console.log("📦 [ROLES CACHE MISS] No cached roles found");
      }

      rolesRequestInFlight.current = true;
      const cancelTokenSource = axios.CancelToken.source();
      axiosSource.current = cancelTokenSource;
      setLoading(true);
      setProgress(0);

      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      console.log('Fetching initial roles (without spotlights)...');

      // 1. Initial fetch for core role data (fast)
      const initialResponse = await axios.get("/api/roles/fetchActiveRoles", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          streams: candidateStream,
          fetchAll: "true",
          includeSpotlights: "false", // <-- Ask for no spotlights
        },
        cancelToken: cancelTokenSource.token,
      });

      const initialRoles = initialResponse.data.roles || [];
      console.log(`Fetched ${initialRoles.length} initial roles.`);

      // Render the page immediately with core data
      setAllRoles(initialRoles);
      setAllRolesLoaded(true);
      setProgress(100);
      setTimeout(() => setLoading(false), 300);

      // 2. Secondary fetch for spotlight details (in background)
      const spotlightIdsToFetch = initialRoles.flatMap(
        (role) => role["Spotlight"] || []
      );

      let finalRoles = initialRoles;

      if (spotlightIdsToFetch.length > 0) {
        console.log(`Fetching ${spotlightIdsToFetch.length} spotlight details in the background...`);
        const spotlightResponse = await axios.post(
          "/api/roles/fetchSpotlightDetails",
          { spotlightIds: spotlightIdsToFetch },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const spotlightMap = spotlightResponse.data.spotlightMap || {};
        console.log(`Received ${Object.keys(spotlightMap).length} spotlight details.`);

        // Merge spotlight details into roles state
        finalRoles = initialRoles.map(role => {
          const sIds = role["Spotlight"] || [];
          const spotlightDetails = sIds.map(id => spotlightMap[id]).filter(Boolean);
          return { ...role, spotlightDetails };
        });

        setAllRoles(finalRoles);
      }

      // Save to cache
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          timestamp: Date.now(),
          roles: finalRoles
        }));
      } catch (e) {
        console.error("Failed to cache roles", e);
      }

    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("Request canceled", err.message);
      } else {
        console.error("Error fetching roles:", err);
        // On error, still mark as loaded to prevent infinite loading
        setAllRolesLoaded(true);
        setProgress(100);
        setTimeout(() => setLoading(false), 300);
      }
    } finally {
      rolesRequestInFlight.current = false;
    }
  };

  // Fetch all roles when candidate stream is available
  useEffect(() => {
    if (candidateStream && !allRolesLoaded) {
      fetchAllRoles();
    }
    // eslint-disable-next-line
  }, [candidateStream]);

  // Reset carousel index when filters change or view mode changes
  useEffect(() => {
    if (viewMode === "carousel") {
      setCarouselIndex(0);
    }
  }, [searchQuery, activeFilters, viewMode]);

  // Keyboard navigation for carousel view
  useEffect(() => {
    if (viewMode !== "carousel") return;

    const handleKeyPress = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (carouselIndex > 0) {
          setCarouselIndex(carouselIndex - 1);
        } else if (currentPage > 1) {
          goToPage(currentPage - 1);
          setCarouselIndex(pageSize - 1);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (carouselIndex < paginatedRoles.length - 1) {
          setCarouselIndex(carouselIndex + 1);
        } else if (hasMore) {
          goToPage(currentPage + 1);
          setCarouselIndex(0);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [viewMode, carouselIndex, paginatedRoles.length, hasMore, currentPage, pageSize]);

  // Client-side pagination - just change page number
  const goToPage = (page) => {
    if (page === currentPage) return;
    if (page < 1 || page > totalPages) return;

    // Track pagination navigation as standalone events
    try {
      const direction = page > currentPage ? 'next' : 'previous';
      trackEvent('Roles Pagination Clicked', {
        direction,
        from_page: currentPage,
        to_page: page,
        page_size: pageSize,
        total_pages: totalPages,
        total_results: totalRolesCount,
      });
    } catch (_) { }

    setCurrentPage(page);

    // Use requestAnimationFrame to ensure the DOM has updated before scrolling
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (exploreRolesRef.current) {
          console.log('Scrolling to explore roles section');
          // Try scrolling to the element with additional offset
          const element = exploreRolesRef.current;
          const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementTop - 20; // 20px offset from top

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        } else {
          console.log('exploreRolesRef.current is null');
        }
      });
    });
  };

  const handleSaveToggle = async (isSaved, roleTitle, roleId) => {
    setSaveNotificationMessage(
      isSaved
        ? `${roleTitle} added to shortlist`
        : `${roleTitle} removed from shortlist`
    );
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 2000);

    // Optimistically update local state
    setShortlistedRoles((prev) => {
      if (isSaved) {
        return [...prev, roleId];
      } else {
        return prev.filter((id) => id !== roleId);
      }
    });
  };

  const handleApplyNow = async (roleId, roleTitle, rationale = null, bypassVideoCheck = false) => {
    // Pre-apply guard: Sales candidates must have a video intro
    const isSales = (candidateStream || "").toLowerCase().includes("sales");
    const isMarketing = (candidateStream || "").toLowerCase().includes("marketing");
    const isGeneralist = (candidateStream || "").toLowerCase().includes("generalist");
    let hasVideo = !!(candidateVideoUrl && String(candidateVideoUrl).trim());

    // Only show video checks if not bypassing
    if (!bypassVideoCheck) {
      if (isSales && !hasVideo) {
        // If video looks missing, do a quick fresh fetch to avoid false negatives from stale cache / race.
        // This is intentionally Sales-only because it's a hard requirement.
        try {
          const freshFields = await fetchCandidateFresh();
          if (freshFields) {
            const freshVideoField =
              freshFields["Video intro (new)"] ||
              freshFields["Raw video intro"] ||
              "";
            const freshVideoUrl = extractVideoIntroUrl(freshVideoField);
            if (freshVideoUrl) {
              setCandidateVideoUrl(freshVideoUrl);
              setCandidateVideoLoaded(true);
              hasVideo = true;
            }
          }
        } catch (_) {}

        if (!hasVideo) {
          setCandidateVideoLoaded(true);
          setShowVideoRequiredModal(true);
          return false;
        }
      }

      // Warning for Marketing and Generalist without video
      if ((isMarketing || isGeneralist) && !hasVideo) {
        setPendingApply({ roleId, roleTitle, rationale });
        setShowVideoWarningModal(true);
        return false;
      }
    }
    // Check if already applied (via applied roles or application pending roles)
    if (appliedRoles.includes(roleId) || applicationPendingRoles.includes(roleId)) {
      setApplyNotification({
        show: true,
        message: `You've already applied to ${roleTitle}!`,
        type: 'info'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
      return false;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const data = {
        fields: {
          Role: [roleId],
          "Match type": "Application",
          Stage: "Application pending",
          ...(rationale && { "Application Rationale": rationale }),
        },
        token: token, // Some APIs might expect the token in the body as well
      };

      const response = await axios.post("/api/interviews/createInterview", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Update the local applied roles lists
      // 1. Update weekly count (for FloatingProgressBar)
      setAppliedRolesThisWeek(prev => [...prev, roleId]);
      // 2. Update all-time list (for filtering browse)
      setAppliedRoles(prev => [...prev, roleId]);
      // 3. Update allInteractedRoles (consistency)
      setAllInteractedRoles(prev => [...prev, roleId]);
      
      console.log("✅ [APPLY] Updated all applied roles lists with roleId:", roleId);

      // Clear shared interview cache so InProcess refetches fresh data
      try {
        clearInterviewsCache();
      } catch (_) { }

      // Clear roles cache to prevent stale data showing role as unapplied
      try {
        const rolesCacheKey = `roles_cache_${candidateStream}`;
        sessionStorage.removeItem(rolesCacheKey);
        console.log('Cleared roles cache after application');
      } catch (_) { }

      // Clear candidate data cache to force refetch of applied roles from Airtable
      try {
        const candidateCacheKey = `candidate_data_cache_${token.slice(0, 32)}`;
        sessionStorage.removeItem(candidateCacheKey);
        console.log('Cleared candidate data cache after application');
      } catch (_) { }

      clearRecommendedRolesCache();
      fetchRecommendedRoles({ bypassCache: true });

      setApplyNotification({
        show: true,
        message: `Successfully applied to ${roleTitle}!`,
        type: 'success'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
      try {
        // Track application submitted only after confirmed success
        trackEvent("Application Submitted", { role_id: roleId, role_title: roleTitle, rationale_provided: !!rationale, page: "roles_list" });
        if (rationale && String(rationale).trim()) {
          // Dedicated event for rationale-required flows (submitted via modal)
          trackRationaleApplicationSubmitted({ role_id: roleId, role_title: roleTitle, page: "roles_list" });
        }
      } catch (_) { }
      return true; // Indicate success

    } catch (err) {
      console.error("Error applying to role:", err);
      let errorMessage = `Error applying to ${roleTitle}.`;
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error; // Use backend error if available
      }
      setApplyNotification({
        show: true,
        message: errorMessage,
        type: 'error'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
      return false; // Indicate failure
    }
  };

  // Handle match request action completion
  const handleMatchRequestActionComplete = (action, roleTitle) => {
    setMatchRequestNotification({
      show: true,
      message: action === 'accepted'
        ? `✅ Great! You've accepted the interview request for ${roleTitle}. Check your email for next steps.`
        : `❌ You've declined the interview request for ${roleTitle}.`,
      type: action === 'accepted' ? 'success' : 'info'
    });
    setTimeout(() => setMatchRequestNotification({ show: false, message: '', type: '' }), 4000);
    refreshMatchRequests(); // Refresh the count for the new banner
  };

  // Handle startup visibility modal completion
  const handleStartupVisibilityComplete = async () => {
    setShowStartupVisibilityModal(false);
    setIsFirstTimeLogin(false); // Mark as no longer first time login

    // Clear the first-time login flag from localStorage
    localStorage.removeItem("isFirstTimeLogin");

    console.log("Startup visibility modal completed - first-time setup done");
  };

  // Check if candidate is NOT Live first. This overrides loading/roles display.
  if (candidateStatus !== null && candidateStatus !== "Live") {
    // Candidate is not Live or status check failed
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4 sm:p-8">
        <div className="w-full max-w-3xl text-center">
          <h2 className="text-2xl font-semibold mb-4">No Roles Available</h2>


        </div>
      </div>
    );
  }


  // Show main loading state while roles are loading OR while interview data is still loading
  // This prevents the race condition where we show incorrect role counts
  const isStillLoadingData = (loading && !allRolesLoaded) || (allRolesLoaded && !allInteractedRolesLoaded);
  
  if (isStillLoadingData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4 sm:p-8">
        <div className="w-full max-w-3xl">
          <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-6">
            <motion.div
              className="absolute left-0 top-0 h-full bg-[#02B491]"
              initial={{ width: 0 }}
              animate={{ width: `${allRolesLoaded ? 80 : progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <p className="text-center text-gray-600 font-medium mb-8">
            {allRolesLoaded ? "Loading your application history..." : "Loading your roles..."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm p-4 animate-pulse"
              >
                <div className="h-32 sm:h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // If no roles are loaded, and user is not filtering, show "No Roles" release message
  const isNotFiltering =
    !searchQuery &&
    activeFilters.workingStyle.length === 0 &&
    activeFilters.savedOnly === false;

  // Only show "No Roles" message after ALL data has loaded (roles + interview exclusion data)
  if (!loading && allRolesLoaded && allInteractedRolesLoaded && filteredAndSortedRoles.length === 0 && isNotFiltering) {
    // Show "No Roles Available" + Next Release info
    const nextReleaseObj = nextReleaseRef.current;
    const today = new Date();
    const timeRemaining = nextReleaseObj
      ? nextReleaseObj.getTime() - today.getTime()
      : 0;

    const daysRemaining = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    const hoursRemaining = Math.floor(
      (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutesRemaining = Math.floor(
      (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
    );

    const isReleaseDay = daysRemaining === 0;
    const isPastReleaseTime = timeRemaining <= 0;

    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border p-6 sm:p-10 w-full max-w-[620px] text-center transition duration-300 hover:shadow-md"
        >
          <div className="flex justify-center">
            <div className="bg-white size-10 sm:size-12 grid place-items-center rounded-xl relative left-2.5 top-1.5 -rotate-6 shadow-lg border border-gray-200">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="bg-white size-10 sm:size-12 grid place-items-center rounded-xl relative z-10 shadow-lg border border-gray-200">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mt-6 sm:mt-8 mb-2 sm:mb-3">
            No roles are live right now.
          </h2>
          <p className="text-gray-600 text-sm sm:text-base max-w-md mx-auto">
            New opportunities go live regularly and always close at midnight on Sunday, so check back soon. In the meantime, why not review your upcoming interviews or brush up on your interview skills!
          </p>
        </motion.div>
      </div>
    );
  }

  // For suggested searches
  const getSuggestedSearches = (query) => {
    if (!query) return [];
    const marketingTerms = [
      "marketing",
      "content",
      "social media",
      "brand",
      "digital",
      "communications",
      "copywriting",
      "advertising",
      "seo",
      "analytics",
    ];
    const lowerQuery = query.toLowerCase();
    return marketingTerms
      .filter((term) => !lowerQuery.includes(term))
      .slice(0, 3);
  };

  const workingStyleOptions = ["Hybrid", "In person", "Remote"];

  const FilterChip = ({ label, active, onClick }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${active
        ? "bg-[#02B491] text-white border-[#02B491] shadow-md"
        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm"
        }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col items-start bg-white min-h-screen pl-0 md:pl-20">
      {/* The InterviewRequestAlert component that was here should be removed */}

      <SaveNotification
        show={showSaveNotification}
        message={saveNotificationMessage}
      />
      {/* Notification for Apply Now status */}
      <SaveNotification
        show={applyNotification.show}
        message={applyNotification.message}
        type={applyNotification.type} // This might require your SaveNotification to handle a 'type' prop for styling
      />
      {/* Sales Video Required Modal - redirect to profile */}
      {showVideoRequiredModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl relative">
            <button
              onClick={() => setShowVideoRequiredModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Add a video intro to apply</h2>
              <p className="text-sm text-gray-600 mt-1">Applications require a short video intro. Please add your video on your profile first.</p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => { setShowVideoRequiredModal(false); navigate('/profile#video'); }}
                className="px-6 py-2 text-sm font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-lg"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Warning Modal for Marketing/Generalist */}
      {showVideoWarningModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl relative">
            <button
              onClick={() => {
                if (!isApplyingFromModal) {
                  setShowVideoWarningModal(false);
                  setPendingApply(null);
                }
              }}
              disabled={isApplyingFromModal}
              className={`absolute top-4 right-4 ${isApplyingFromModal
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">🚨 We strongly recommend a video intro</h2>
              <p className="text-sm text-gray-600 mt-1">
                Our recent data shows that you are <span className="font-semibold">8x more likely</span> to get an <span className="font-semibold">offer</span> if you have an intro video
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={async () => {
                  if (pendingApply && !isApplyingFromModal) {
                    setIsApplyingFromModal(true);
                    const success = await handleApplyNow(pendingApply.roleId, pendingApply.roleTitle, pendingApply.rationale, true);
                    setIsApplyingFromModal(false);
                    if (success) {
                      setShowVideoWarningModal(false);
                      setPendingApply(null);
                    }
                  }
                }}
                disabled={isApplyingFromModal}
                className={`px-6 py-2 text-sm font-medium rounded-lg flex items-center gap-2 ${isApplyingFromModal
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  }`}
              >
                {isApplyingFromModal && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isApplyingFromModal ? 'Applying...' : 'Apply anyway'}
              </button>
              <button
                onClick={() => { setShowVideoWarningModal(false); setPendingApply(null); navigate('/profile#video'); }}
                disabled={isApplyingFromModal}
                className={`px-6 py-2 text-sm font-medium rounded-lg ${isApplyingFromModal
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-white bg-[#02B491] hover:bg-[#018268]'
                  }`}
              >
                Add video first
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification for Match Request actions */}
      <SaveNotification
        show={matchRequestNotification.show}
        message={matchRequestNotification.message}
        type={matchRequestNotification.type}
      />

      {/* Match Request Modal */}
      {showMatchRequestModal && (
        <MatchRequestNotification
          onClose={() => setShowMatchRequestModal(false)}
          onActionComplete={handleMatchRequestActionComplete}
        />
      )}

      {/* Welcome Modal - First Time Login */}
      {showWelcomeModal && (
        <WelcomeModal
          isOpen={showWelcomeModal}
          onClose={() => setShowWelcomeModal(false)}
          userName={userName}
        />
      )}

      {/* Startup Visibility Modal - First Time Login */}
      {showStartupVisibilityModal && (
        <StartupVisibilityModal
          isOpen={showStartupVisibilityModal}
          onClose={() => setShowStartupVisibilityModal(false)}
          onComplete={handleStartupVisibilityComplete}
        />
      )}
      <div className="w-full p-4 sm:p-6 md:p-12 flex flex-col h-full max-w-[1800px] mx-auto pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 md:mb-8 px-4 sm:px-0"
        >
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 sm:mb-6">
            {userName ? `Welcome back,  ${userName}!` : ''}
          </h1>

          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
            {/* NEW: Integrated Interview Request Section */}
            {!matchRequestsLoading && pendingCount > 0 && (
              <div className="bg-red-50 border-b border-red-200 p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 flex-grow min-w-0">
                    <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C13.1 2 14 2.9 14 4V5.08C16.84 5.56 19 8.03 19 11V16L21 18V19H3V18L5 16V11C5 8.03 7.16 5.56 10 5.08V4C10 2.9 10.9 2 12 2ZM12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z" />
                      </svg>
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="text-sm font-semibold text-red-700 truncate">
                        {pendingCount} interview request{pendingCount !== 1 ? 's' : ''} waiting!
                      </p>
                      <p className="text-xs text-red-600 hidden sm:block truncate">
                        Companies want to interview you - respond quickly.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMatchRequestModal(true)}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    Review Now
                  </button>
                </div>
              </div>
            )}
            {/* Existing content of the banner */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 sm:p-6 gap-6 md:gap-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-[#02B491]/10 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#02B491]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    Application Deadline
                  </h3>
                  <p className="text-sm text-gray-800">
                    <span className="font-bold text-[#02B491]">
                      {nextDeadlineDate}
                    </span>{" "}
                    at midnight
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-[#02B491]/10 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#02B491]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    Maximize Your Chances
                  </h3>
                  <p className="text-sm text-gray-600">
                      Apply to {appliedRolesThisWeekLoaded ? `${Math.max(0, 7 - appliedRolesThisWeek.length)} more` : '7'} roles this week to hit your target
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowHowToApply(true)}
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#02B491] text-white rounded-lg hover:bg-[#029078] transition-colors text-sm font-medium whitespace-nowrap shadow-sm hover:shadow md:ml-4"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                View Application Guide
              </button>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {showHowToApply && (
            <HowToApplyModal
              isOpen={showHowToApply}
              onClose={() => setShowHowToApply(false)}
              candidateStream={candidateStream}
            />
          )}
        </AnimatePresence>

        {/* AI Recommended Roles - Hidden in carousel mode */}
        {(() => {
          console.log('=== Recommended Roles Section ===');
          console.log('Loading state:', loading);
          console.log('Roles loaded:', allRoles.length);
          console.log('Shortlisted roles loaded:', shortlistedRolesLoaded);

          // Always show recommendations section when page is fully loaded (except in carousel mode)
          if (!loading && viewMode !== "carousel") {
            console.log('Rendering RecommendedRoles component');
            return (
              <RecommendedRoles
                recommendedRoles={recommendedRoles}
                recommendedRolesLoading={recommendedRolesLoading}
                recommendedRolesError={recommendedRolesError}
                onSaveToggle={handleSaveToggle}
                shortlistedRoles={shortlistedRoles}
                shortlistedRolesLoaded={shortlistedRolesLoaded}
                onApplyNow={handleApplyNow}
                appliedRoles={appliedRoles}
                appliedRolesLoaded={appliedRolesLoaded}
                interviewingRoles={interviewingRoles}
                interviewingRolesLoaded={interviewingRolesLoaded}
                unsuccessfulRoles={unsuccessfulRoles}
                unsuccessfulRolesLoaded={unsuccessfulRolesLoaded}
                applicationPendingRoles={applicationPendingRoles}
                applicationPendingRolesLoaded={applicationPendingRolesLoaded}
                candidateData={{ stream: candidateStream, video: candidateVideoUrl }}
                onRequireVideo={(roleId, roleTitle, rationale) => {
                  const isSales = (candidateStream || "").toLowerCase().includes("sales");
                  const isMarketing = (candidateStream || "").toLowerCase().includes("marketing");
                  const isGeneralist = (candidateStream || "").toLowerCase().includes("generalist");

                  if (isSales) {
                    setShowVideoRequiredModal(true);
                  } else if (isMarketing || isGeneralist) {
                    setPendingApply({ roleId, roleTitle, rationale });
                    setShowVideoWarningModal(true);
                  }
                }}
              />
            );
          } else {
            console.log('Not rendering recommendations yet - page is still loading');
            return null;
          }
        })()}

        {/* --- Explore Roles Section Header --- */}
        {viewMode !== "carousel" && (
          <div ref={exploreRolesRef} className="flex justify-between items-center mt-8 mb-4 sm:mb-6 px-4 sm:px-0">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Explore All Roles</h2>
            {/* Optional: Add a count or other info here */}
            <span className="text-sm text-gray-600">
              Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalRolesCount)} of {totalRolesCount}
            </span>
          </div>
        )}

        {/* Sticky Search/Filter Bar - Hidden in carousel mode */}
        {viewMode !== "carousel" && (
          <div
            ref={searchFilterRef}
            className={`w-full transition-all duration-300 px-4 sm:px-0 ${isSearchSticky
              ? "sticky top-0 py-4 z-40 bg-white/80 backdrop-blur-sm border-b border-gray-100" // Adjusted for new design
              : "mb-4"
              }`}
          >
            <div className="max-w-[1800px] mx-auto">
              <div className="flex flex-col gap-4">
                {/* Search Input */}
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-lg focus:ring-2 focus:ring-[#02B491]/20 focus:border-[#02B491] transition-all duration-200 text-gray-900 placeholder-gray-500 shadow-sm"
                    placeholder="Search by title, company, or keyword..."
                    value={tempSearchQuery}
                    onChange={(e) => setTempSearchQuery(e.target.value)}
                  />
                </div>

                {/* Filters Bar */}
                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="flex items-center gap-2 flex-wrap pb-4">
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                          <span className="font-medium">Filters:</span>
                        </div>
                        <FilterButton
                          label="My saved roles"
                          active={activeFilters.savedOnly}
                          onClick={() => {
                            setActiveFilters(prev => ({ ...prev, savedOnly: !prev.savedOnly }));
                            setCurrentPage(1);
                            const chips = [
                              ...(!activeFilters.savedOnly ? ["Saved"] : []),
                              ...activeFilters.workingStyle,
                            ];
                            trackEngagementAction("filter_toggled", { chip: "Saved", active: !activeFilters.savedOnly, chips });
                            try {
                              trackEvent('Search or Filter Used', {
                                action: 'filter_toggled',
                                chip: 'Saved',
                                active: !activeFilters.savedOnly,
                                chips,
                                page: 'roles_list',
                              });
                            } catch (_) { }
                          }}
                        />
                        {workingStyleOptions.map((style) => (
                          <FilterButton
                            key={style}
                            label={style}
                            active={activeFilters.workingStyle.includes(style)}
                            onClick={() => {
                              const newStyles = activeFilters.workingStyle.includes(style)
                                ? activeFilters.workingStyle.filter((s) => s !== style)
                                : [...activeFilters.workingStyle, style];
                              setActiveFilters(prev => ({ ...prev, workingStyle: newStyles }));
                              setCurrentPage(1);
                              const chips = [
                                ...(activeFilters.savedOnly ? ["Saved"] : []),
                                ...newStyles,
                              ];
                              trackEngagementAction("filter_toggled", { chip: style, active: newStyles.includes(style), chips });
                              try {
                                trackEvent('Search or Filter Used', {
                                  action: 'filter_toggled',
                                  chip: style,
                                  active: newStyles.includes(style),
                                  chips,
                                  page: 'roles_list',
                                });
                              } catch (_) { }
                            }}
                          />
                        ))}
                        {(searchQuery || activeFilters.workingStyle.length > 0 || activeFilters.savedOnly) && (
                          <button onClick={handleClearAll} className="text-sm text-gray-500 hover:text-gray-800 font-medium">
                            Clear all
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Sticky Bottom Bar */}
                <div className="flex items-center justify-between">
                  {/* Left side: Results count and filter toggle */}
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600">
                      {allRolesLoaded ? (
                        <>
                          Showing <strong>{totalRolesCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} - {Math.min(currentPage * pageSize, totalRolesCount)}</strong> of <strong>{totalRolesCount}</strong> results
                        </>
                      ) : (
                        "Loading roles..."
                      )}
                    </div>

                    {isSearchSticky && (
                      <button onClick={() => setShowFilters(prev => !prev)} className="font-medium text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                        {showFilters ? 'Hide' : 'Show'} Filters
                      </button>
                    )}
                  </div>

                  {/* Right side: View mode toggle */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                      <button onClick={() => setViewMode("grid")} className={`p-2 rounded-md transition-colors ${viewMode === "grid" ? "bg-white shadow" : "text-gray-500 hover:bg-gray-200"}`} aria-label="Grid View" title="Grid View">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                      </button>
                      <button onClick={() => {
                        setViewMode("carousel");
                        setCarouselIndex(0);
                        try {
                          trackEvent('Carousel View Clicked', {
                            from_view: viewMode,
                            total_roles: totalRolesCount,
                            current_page: currentPage,
                            has_active_filters: searchQuery.length > 0 || activeFilters.workingStyle.length > 0 || activeFilters.savedOnly
                          });
                        } catch (_) { }
                      }} className={`p-2 rounded-md transition-colors ${viewMode === "carousel" ? "bg-white shadow" : "text-gray-500 hover:bg-gray-200"}`} aria-label="Carousel View" title="Carousel View - One at a time">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Carousel View Header - Only shown in carousel mode */}
        {viewMode === "carousel" && (
          <div className="flex items-center mt-8 mb-6 px-4 sm:px-0">
            <button
              onClick={() => setViewMode("grid")}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Back to Grid View</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>
        )}

        {/* Roles display */}
        <AnimatePresence>
          {viewMode === "carousel" ? (
            // CAROUSEL VIEW - One role at a time with navigation
            <div className="w-full mt-6 px-4 sm:px-0 relative">
              <div className="max-w-5xl mx-auto">
                {loading ? (
                  // Skeleton for carousel
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="h-96 bg-gray-100 rounded-lg mb-4 animate-pulse"></div>
                    <div className="h-8 bg-gray-100 rounded w-3/4 mb-3 animate-pulse"></div>
                    <div className="h-6 bg-gray-100 rounded w-1/2 animate-pulse"></div>
                  </div>
                ) : paginatedRoles.length > 0 ? (
                  <>
                    {/* Navigation Arrows */}
                    <div className="flex items-center justify-between gap-4">
                      <button
                        onClick={() => {
                          if (carouselIndex > 0) {
                            setCarouselIndex(carouselIndex - 1);
                          } else if (currentPage > 1) {
                            goToPage(currentPage - 1);
                            setCarouselIndex(pageSize - 1);
                          }
                        }}
                        disabled={carouselIndex === 0 && currentPage === 1}
                        className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all ${carouselIndex === 0 && currentPage === 1
                          ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                          : "bg-white border-2 border-[#02B491] text-[#02B491] hover:bg-[#02B491] hover:text-white shadow-md hover:scale-110"
                          }`}
                        aria-label="Previous role"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {/* Role Card */}
                      <motion.div
                        key={paginatedRoles[carouselIndex]?.id}
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.3 }}
                        className="flex-1"
                      >
                        <RoleCard
                          role={paginatedRoles[carouselIndex]}
                          currentlyPlaying={currentlyPlaying}
                          setCurrentlyPlaying={setCurrentlyPlaying}
                          viewMode="carousel"
                          onSaveToggle={(isSaved, roleTitle) =>
                            handleSaveToggle(isSaved, roleTitle, paginatedRoles[carouselIndex].id)
                          }
                          shortlistedRoles={shortlistedRoles}
                          shortlistedRolesLoaded={shortlistedRolesLoaded}
                          onApplyNow={handleApplyNow}
                          appliedRoles={appliedRoles}
                          appliedRolesLoaded={appliedRolesLoaded}
                          interviewingRoles={interviewingRoles}
                          interviewingRolesLoaded={interviewingRolesLoaded}
                          unsuccessfulRoles={unsuccessfulRoles}
                          unsuccessfulRolesLoaded={unsuccessfulRolesLoaded}
                          applicationPendingRoles={applicationPendingRoles}
                          applicationPendingRolesLoaded={applicationPendingRolesLoaded}
                          candidateData={{ stream: candidateStream, video: candidateVideoUrl }}
                          onRequireVideo={(roleId, roleTitle, rationale) => {
                            const isSales = (candidateStream || "").toLowerCase().includes("sales");
                            const isMarketing = (candidateStream || "").toLowerCase().includes("marketing");
                            const isGeneralist = (candidateStream || "").toLowerCase().includes("generalist");

                            if (isSales) {
                              setShowVideoRequiredModal(true);
                            } else if (isMarketing || isGeneralist) {
                              setPendingApply({ roleId, roleTitle, rationale });
                              setShowVideoWarningModal(true);
                            }
                          }}
                        />
                      </motion.div>

                      <button
                        onClick={() => {
                          if (carouselIndex < paginatedRoles.length - 1) {
                            setCarouselIndex(carouselIndex + 1);
                          } else if (hasMore) {
                            goToPage(currentPage + 1);
                            setCarouselIndex(0);
                          }
                        }}
                        disabled={carouselIndex === paginatedRoles.length - 1 && !hasMore}
                        className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all ${carouselIndex === paginatedRoles.length - 1 && !hasMore
                          ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                          : "bg-white border-2 border-[#02B491] text-[#02B491] hover:bg-[#02B491] hover:text-white shadow-md hover:scale-110"
                          }`}
                        aria-label="Next role"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Carousel Counter and Navigation Dots */}
                    <div className="text-center mt-6 space-y-4">
                      {/* Page Dots Indicator */}
                      <div className="flex items-center justify-center gap-2">
                        {paginatedRoles.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCarouselIndex(index)}
                            className={`transition-all duration-200 rounded-full ${index === carouselIndex
                              ? "w-8 h-2 bg-[#02B491]"
                              : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                              }`}
                            aria-label={`Go to role ${index + 1}`}
                          />
                        ))}
                      </div>

                      {/* Counter Text */}
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold text-[#02B491]">
                          {(currentPage - 1) * pageSize + carouselIndex + 1}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold">{totalRolesCount}</span> roles
                      </p>

                      {/* Keyboard Shortcut Hint */}

                    </div>
                  </>
                ) : (
                  // No roles found
                  <motion.div
                    className="py-12 text-center rounded-xl bg-gray-50 border border-gray-100"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="max-w-lg mx-auto px-6">
                      <svg
                        className="w-16 h-16 text-gray-300 mx-auto mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
                        />
                      </svg>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">
                        No roles found
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Try adjusting your filters to see more results.
                      </p>
                      <button
                        onClick={handleClearAll}
                        className="px-4 py-2 bg-[#02B491] text-white rounded-lg hover:bg-[#029478] transition-colors"
                      >
                        Clear all filters
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          ) : (
            // GRID AND LIST VIEWS
            <motion.div
              ref={rolesGridRef}
              layout
              className="w-full mt-6 px-4 sm:px-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
            >
              {loading
                ? // Skeleton placeholders
                Array.from({ length: 6 }).map((_, index) => (
                  <motion.div
                    key={`skeleton-${index}`}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-white rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] p-4"
                  >
                    <div className="h-36 sm:h-48 bg-gray-100 rounded-lg mb-4"></div>
                    <div className="h-6 bg-gray-100 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                  </motion.div>
                ))
                : paginatedRoles.length > 0
                  ? paginatedRoles.map((role, index) => (
                    <motion.div
                      key={role.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className="relative"
                      ref={(el) => {
                        if (index < 3) {
                          topRowRefs.current[index] = el;
                          if (el) el.setAttribute('data-top-index', String(index));
                        }
                      }}
                    >
                      {/* Modified positioning to avoid overlapping */}
                      <div className="relative">
                        <RoleCard
                          role={role}
                          currentlyPlaying={currentlyPlaying}
                          setCurrentlyPlaying={setCurrentlyPlaying}
                          viewMode={viewMode}
                          onSaveToggle={(isSaved, roleTitle) =>
                            handleSaveToggle(isSaved, roleTitle, role.id)
                          }
                          shortlistedRoles={shortlistedRoles}
                          shortlistedRolesLoaded={shortlistedRolesLoaded}
                          onApplyNow={handleApplyNow}
                          appliedRoles={appliedRoles}
                          appliedRolesLoaded={appliedRolesLoaded}
                          interviewingRoles={interviewingRoles}
                          interviewingRolesLoaded={interviewingRolesLoaded}
                          unsuccessfulRoles={unsuccessfulRoles}
                          unsuccessfulRolesLoaded={unsuccessfulRolesLoaded}
                          applicationPendingRoles={applicationPendingRoles}
                          applicationPendingRolesLoaded={applicationPendingRolesLoaded}
                          candidateData={{ stream: candidateStream, video: candidateVideoUrl }}
                          onRequireVideo={(roleId, roleTitle, rationale) => {
                            const isSales = (candidateStream || "").toLowerCase().includes("sales");
                            const isMarketing = (candidateStream || "").toLowerCase().includes("marketing");
                            const isGeneralist = (candidateStream || "").toLowerCase().includes("generalist");

                            if (isSales) {
                              setShowVideoRequiredModal(true);
                            } else if (isMarketing || isGeneralist) {
                              setPendingApply({ roleId, roleTitle, rationale });
                              setShowVideoWarningModal(true);
                            }
                          }}
                        />
                      </div>
                    </motion.div>
                  ))
                  : // No roles found with the current filters
                  <motion.div
                    className="col-span-full py-8 sm:py-12 text-center rounded-xl bg-gray-50 border border-gray-100"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="max-w-lg mx-auto px-4 sm:px-6">
                      <svg
                        className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
                        />
                      </svg>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                        No roles found matching your criteria
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {searchQuery ? (
                          <>
                            We couldn't find any roles matching{" "}
                            <span className="font-medium">"{searchQuery}"</span>.
                            {allRoles.length > 0 && (
                              <span className="block mt-1 text-sm">
                                Try different keywords or remove some filters.
                              </span>
                            )}
                          </>
                        ) : (
                          <>Try adjusting your filters to see more results.</>
                        )}
                      </p>
                      {searchQuery && getSuggestedSearches(searchQuery).length > 0 && (
                        <div className="mb-6">
                          <p className="text-sm text-gray-500 mb-2">
                            Try searching for:
                          </p>
                          <div className="flex flex-wrap justify-center gap-2">
                            {getSuggestedSearches(searchQuery).map((term) => (
                              <button
                                key={term}
                                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-full text-sm text-gray-700 transition-colors"
                                onClick={() => {
                                  setTempSearchQuery(term);
                                  setSearchQuery(term);
                                  setCurrentPage(1);
                                }}
                              >
                                {term}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={handleClearAll}
                        className="px-4 py-2 bg-[#02B491] text-white rounded-lg hover:bg-[#029478] transition-colors duration-200"
                      >
                        Clear all filters
                      </button>
                    </div>
                  </motion.div>
              }
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pagination - Hide in carousel view */}
        {viewMode !== "carousel" && allRolesLoaded && filteredAndSortedRoles.length > 0 && (
          <div className="flex justify-center items-center gap-4 mt-8 mb-8 px-4 sm:px-0">
            <nav className="flex items-center gap-2 md:gap-4 w-full max-w-xs md:max-w-none justify-between md:justify-center">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 md:px-6 py-2 rounded-lg border ${currentPage === 1
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-gray-200 text-gray-700 hover:border-gray-300"
                  } text-sm font-medium transition-all duration-150 ease-in-out bg-white min-w-[100px]`}
              >
                Previous
              </button>
              <div className="text-sm text-gray-600 hidden md:block">
                Page {currentPage} of {totalPages}
              </div>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={!hasMore}
                className={`px-4 md:px-6 py-2 rounded-lg border ${!hasMore
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-gray-200 text-gray-700 hover:border-gray-300"
                  } text-sm font-medium transition-all duration-150 ease-in-out bg-white min-w-[100px]`}
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Floating Progress Bar - Uses WEEKLY applied roles, not all-time */}
      {appliedRolesThisWeekLoaded && candidateStatus === "Live" && !hideFloatingCounter && (() => {
        // Use appliedRolesThisWeek (from "Applied this week drop" field)
        // This is the CORRECT count for weekly progress
        const weeklyCount = appliedRolesThisWeek.length;
        
        // DEBUGGING: Log the calculation
        console.log("📊 [FloatingProgressBar Render] WEEKLY Calculation:");
        console.log("  - appliedRolesThisWeek.length (CORRECT weekly count):", weeklyCount);
        console.log("  - appliedRoles.length (ALL TIME - for filtering only):", appliedRoles.length);
        console.log("  - unsuccessfulRoles.length:", unsuccessfulRoles.length);
        
        if (weeklyCount > 20) {
          console.error("🚨 [FloatingProgressBar Render] CRITICAL: Weekly count is too high!");
          console.error("  - First 10 appliedRolesThisWeek:", appliedRolesThisWeek.slice(0, 10));
        }
        
        return (
        <FloatingProgressBar
            appliedCount={weeklyCount}
          target={7}
        />
        );
      })()}

      {/* Help Panel Component */}
      <HelpPanel candidateStream={candidateStream} />


    </div>
  );
}

export default RolesList;
